import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, EyeOff } from 'lucide-react';
import { useVenue } from '../../contexts/VenueContext';
import { setVenueSetupReturnPath } from '../../utils/venueSetupBackNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";

/**
 * "Your venue is hidden from guests" banner for ALREADY-LIVE venues
 * that the backend has auto-hidden (`status=live`, `is_active=false`)
 * because the owner un-completed a required step after going live —
 * removed photos, cleared operating hours, disconnected a payout, etc.
 *
 * Lists each specific missing piece so the owner knows exactly what
 * to fix. Lives next to `VenueActionNeededBanner` so the owner sees
 * the same message no matter which tab they're on.
 */
export function VenueLiveButHiddenBanner({ fromPath, testIdPrefix }) {
  const navigate = useNavigate();
  const { activeVenueAppId, activeVenue } = useVenue();
  const [payoutStatus, setPayoutStatus] = useState(null);

  useEffect(() => {
    if (!activeVenueAppId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/venue/payout-method/${activeVenueAppId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setPayoutStatus(res.data || null);
      } catch {
        if (!cancelled) setPayoutStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, [activeVenueAppId]);

  if (activeVenue?.status !== 'live') return null;
  if (activeVenue?.is_active !== false) return null;

  const photoCount = Array.isArray(activeVenue?.photo_urls) ? activeVenue.photo_urls.length : 0;
  const opHours = activeVenue?.operating_hours || {};
  const hasHours = Object.values(opHours).some((v) => !!v);
  const categoryCount = Array.isArray(activeVenue?.venue_categories) ? activeVenue.venue_categories.length : 0;
  const hasDescription = Boolean((activeVenue?.map_description || '').trim());
  const tagCount = Array.isArray(activeVenue?.venue_tags) ? activeVenue.venue_tags.length : 0;
  const vibeComplete = categoryCount > 0 && hasDescription && tagCount > 0;
  const payoutMissing = payoutStatus && payoutStatus.is_on_hold;
  const missing = [];
  if (photoCount < 10) missing.push(`add ${10 - photoCount} more photo${(10 - photoCount) === 1 ? '' : 's'}`);
  if (!vibeComplete) {
    const missingVibe = [];
    if (!categoryCount) missingVibe.push('a category');
    if (!hasDescription) missingVibe.push('a short description');
    if (!tagCount) missingVibe.push('at least 1 tag');
    missing.push(`finish your vibe (${missingVibe.join(', ')})`);
  }
  if (!hasHours) missing.push('set your operating hours');
  if (payoutMissing) missing.push('reconnect a payout method');
  if (missing.length === 0) return null;

  const handleClick = () => {
    setVenueSetupReturnPath(fromPath);
    navigate('/venue/setup-mode', { state: { from: fromPath } });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={`${testIdPrefix}-missing-setup-banner`}
      style={{
        width: 'calc(100% - 32px)',
        maxWidth: 360,
        marginTop: 12,
        padding: '12px 14px',
        borderRadius: 14,
        border: '1px solid rgba(214, 137, 16, 0.25)',
        background: 'var(--status-warning-surface)',
        boxShadow: '0 8px 18px rgba(214, 137, 16, 0.12)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <span style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(214, 137, 16, 0.14)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <EyeOff size={16} color="#B56B11" strokeWidth={2.2} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontFamily: FONT, fontSize: 14, fontWeight: 700, color: 'var(--status-warning-text)' }}>
          Your venue is hidden from guests
        </p>
        <p
          data-testid={`${testIdPrefix}-missing-setup-detail`}
          style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--status-warning-text)', lineHeight: 1.45, fontWeight: 500, opacity: 0.9 }}
        >
          To reappear on the guest map you need to {missing.join(', ')}.
        </p>
        <span style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 9999, background: 'linear-gradient(135deg, #F39A24, #D6890F)', color: '#FFFFFF', fontFamily: FONT, fontSize: 12, fontWeight: 700, boxShadow: '0 8px 16px rgba(214, 137, 16, 0.30)' }}>
          Fix to go live
          <ArrowRight size={12} strokeWidth={2.4} />
        </span>
      </div>
    </button>
  );
}

export default VenueLiveButHiddenBanner;
